import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Pencil, X } from "lucide-react";
import { updatePlatformSettings } from "@/services/channelCenter";
import type { PlatformDetail } from "@/services/types/channelCenter";

/**
 * PLATEDIT-1 · 平台建好后改「显示名 / 平台供应商 ID」(mode=basic) 或「出站根地址」(mode=outbound)。
 * 只提交改动过的字段；交易闸门 live 时改动立即生效 → 红字提醒 + 必须多勾一个「我确认」才能保存。
 * 不涉及任何凭据（凭据走各自的专用弹窗）。
 */
export function PlatformSettingsModal({ platform, mode, onClose }: { platform: PlatformDetail; mode: "basic" | "outbound"; onClose: () => void }) {
  const qc = useQueryClient();
  const live = platform.status === "live";
  const [name, setName] = useState(platform.display_name);
  const [supplierId, setSupplierId] = useState(platform.supplier_id ?? "");
  const [url, setUrl] = useState(platform.outbound_base_url ?? "");
  const [confirmed, setConfirmed] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (payload: { display_name?: string; platform_supplier_id?: string; outbound_base_url?: string }) => updatePlatformSettings(platform.code, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channel-center"] });
      onClose();
    },
  });

  function buildPayload(): { display_name?: string; platform_supplier_id?: string; outbound_base_url?: string } {
    const payload: { display_name?: string; platform_supplier_id?: string; outbound_base_url?: string } = {};
    if (mode === "outbound") {
      if (url.trim() !== (platform.outbound_base_url ?? "")) payload.outbound_base_url = url.trim();
    } else {
      if (name.trim() !== platform.display_name) payload.display_name = name.trim();
      if (platform.capabilities.needs_supplier_id && supplierId.trim() !== (platform.supplier_id ?? "")) payload.platform_supplier_id = supplierId.trim();
    }
    return payload;
  }

  const payload = buildPayload();
  const changed = Object.keys(payload).length > 0;
  const canSave = changed && (!live || confirmed) && !mut.isPending && (mode === "outbound" || name.trim() !== "");

  function save() {
    setLocalError(null);
    if (payload.outbound_base_url && !/^https?:\/\//i.test(payload.outbound_base_url)) {
      setLocalError("出站根地址必须以 http:// 或 https:// 开头");
      return;
    }
    mut.mutate(payload);
  }

  const inputCls = "rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-blue-500";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div role="dialog" className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><Pencil size={18} className="text-blue-600" /> {mode === "outbound" ? "修改出站根地址" : "编辑基础信息"}</h2>
          <button type="button" aria-label="关闭" onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          {mode === "outbound" ? (
            <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
              出站根地址
              <input data-testid="settings-url" name="outbound-base-url" autoComplete="off" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://tourapi.example.com/api" className={`${inputCls} font-mono`} />
              <span className="text-[11.5px] font-normal text-slate-400">必须以 http:// 或 https:// 开头，末尾的 / 会自动去掉；不要写账号密码（账号密码在「更新凭据」里配）。留空 = 清空，该平台不再出站。</span>
            </label>
          ) : (
            <>
              <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
                显示名
                <input data-testid="settings-name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
              </label>
              {platform.capabilities.needs_supplier_id && (
                <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
                  平台供应商 ID（Supplier ID）
                  <input data-testid="settings-supplier-id" maxLength={120} autoComplete="off" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputCls} />
                  <span className="text-[11.5px] font-normal text-slate-400">对方平台分配给我们的供应商编号；留空 = 清空。</span>
                </label>
              )}
            </>
          )}

          {live && (
            <div data-testid="settings-live-warning" className="rounded-lg border border-rose-200 bg-rose-50 p-3">
              <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-rose-600"><AlertTriangle size={14} /> 已开闸，改了立即生效</p>
              <label className="mt-2 flex items-center gap-2 text-[12.5px] text-rose-700">
                <input data-testid="settings-confirm" type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                我确认
              </label>
            </div>
          )}

          {(localError || mut.isError) && (
            <p role="alert" data-testid="settings-error" className="text-[12.5px] text-rose-600">{localError ?? (mut.error as Error | null)?.message ?? "保存失败，请重试"}</p>
          )}
          <div className="mt-1 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">取消</button>
            <button type="button" data-testid="settings-save" disabled={!canSave} onClick={save} className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {mut.isPending ? "保存中..." : "保存"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
